import { useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;

export function OccupancyDiagnosticPanel() {
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(currentMonth);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const runDiagnostic = async () => {
    setLoading(true);
    setResults(null);
    try {
      const { data } = await diagnosticsAPI.occupancy({ year, month });
      setResults(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const sortedProperties = results?.properties
    ? [...results.properties].sort((a, b) => {
        if (a.discrepancy && !b.discrepancy) return -1;
        if (!a.discrepancy && b.discrepancy) return 1;
        return a.name.localeCompare(b.name);
      })
    : [];

  const discrepancyCount = results?.discrepant_property_months ?? 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Occupancy Diagnostic</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Controls */}
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="text-sm font-medium text-muted-foreground block mb-1">Year</label>
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            >
              {Array.from({ length: 5 }, (_, i) => currentYear - 2 + i).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-muted-foreground block mb-1">Month</label>
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>
                  {new Date(2024, m - 1).toLocaleString('default', { month: 'long' })}
                </option>
              ))}
            </select>
          </div>
          <Button onClick={runDiagnostic} disabled={loading}>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Search className="h-4 w-4 mr-2" />
            )}
            Run Diagnostic
          </Button>
        </div>

        {/* Results */}
        {results && (
          <div className="space-y-3">
            {/* Summary */}
            <p className="text-sm font-medium">
              {sortedProperties.length} properties{' · '}
              <span className={discrepancyCount > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}>
                {discrepancyCount} discrepancies found
              </span>
            </p>

            {/* Table */}
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left px-3 py-2 font-medium">Property</th>
                    <th className="text-right px-3 py-2 font-medium">Available</th>
                    <th className="text-right px-3 py-2 font-medium">Booked</th>
                    <th className="text-right px-3 py-2 font-medium">Occupancy</th>
                    <th className="text-center px-3 py-2 font-medium">Status</th>
                    <th className="text-left px-3 py-2 font-medium">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedProperties.map((p, i) => (
                    <tr key={`${p.property_id}-${p.month}-${i}`} className="border-b last:border-0">
                      <td className="px-3 py-2 font-medium">{p.name}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{p.available_nights}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{p.total_nights_booked}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{Math.round(p.occupancy_pct)}%</td>
                      <td className="px-3 py-2 text-center">
                        {p.discrepancy ? (
                          <Badge variant="destructive" className="text-xs">YES</Badge>
                        ) : (
                          <Badge className="text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">OK</Badge>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground max-w-[300px] truncate">
                        {p.discrepancy ? p.discrepancy_reasons?.join('; ') : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
